import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ws } from "@/lib/ws";
import type { CustomRoleMeta, CustomRoleScope } from "@shared/protocol";
import { CUSTOM_ROLE_SCOPES } from "@shared/protocol";
import { relativeTime } from "@/lib/utils";

/** Super-admin-only. Default roles (Owner/Admin/Member/Super admin) can't
 * be edited here — this page is only for the extra, custom-scoped roles a
 * super org can layer on top of a member's base role. */
export function RolesPage() {
  const [roles, setRoles] = useState<CustomRoleMeta[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<CustomRoleScope[]>([]);

  useEffect(() => {
    const offState = ws.on("roles.state", (m) => setRoles(m.roles));
    const offDeleted = ws.on("roles.deleted", (m) =>
      setRoles((prev) => prev.filter((r) => r.id !== m.id)),
    );
    const offErr = ws.on("error", (m) => {
      if (m.req_type.startsWith("roles.")) setError(m.message);
    });
    ws.send({ type: "roles.list" });
    return () => {
      offState();
      offDeleted();
      offErr();
    };
  }, []);

  function toggleScope(scope: CustomRoleScope) {
    setScopes((cur) =>
      cur.includes(scope) ? cur.filter((s) => s !== scope) : [...cur, scope],
    );
  }

  function handleCreate() {
    if (!name.trim() || scopes.length === 0) return;
    ws.send({ type: "roles.create", name: name.trim(), scopes });
    setShowCreate(false);
    setName("");
    setScopes([]);
  }

  function handleDelete(id: string) {
    if (
      !confirm(
        "Delete this role? Members holding it lose its extra scopes immediately.",
      )
    )
      return;
    ws.send({ type: "roles.delete", id });
  }

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Roles</h1>
          <p className="text-sm text-muted-foreground">
            Custom roles grant extra scopes on top of a member&apos;s
            Owner/Admin/Member role. Assign them from the{" "}
            <span className="font-medium">Members</span> page.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>New role</Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Custom roles</CardTitle>
        </CardHeader>
        <CardContent>
          {roles.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No custom roles yet.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Scopes</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {r.scopes.map((s) => (
                          <Badge
                            key={s}
                            variant="secondary"
                            className="font-mono text-[10px]"
                          >
                            {s}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {relativeTime(r.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(r.id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create role</DialogTitle>
            <DialogDescription>
              Pick a name and the scopes it grants.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 my-4">
            <Label htmlFor="rolename">Name</Label>
            <Input
              id="rolename"
              placeholder="Build engineer"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2 mb-4">
            <Label>Scopes</Label>
            <div className="space-y-2 mt-1">
              {CUSTOM_ROLE_SCOPES.map((s) => (
                <label
                  key={s.value}
                  htmlFor={`role-scope-${s.value}`}
                  className="flex items-start gap-2"
                >
                  <input
                    type="checkbox"
                    id={`role-scope-${s.value}`}
                    className="mt-0.5"
                    checked={scopes.includes(s.value)}
                    onChange={() => toggleScope(s.value)}
                  />
                  <span className="leading-tight">
                    <span className="font-mono text-xs">{s.value}</span>
                    <span className="block text-xs text-muted-foreground">
                      {s.description}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            {scopes.length === 0 && (
              <p className="text-xs text-destructive">
                Select at least one scope.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!name.trim() || scopes.length === 0}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
